import {
  Control,
  useController,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { useIntl } from 'react-intl';
import { getIDFromUrl, Url } from '@rossum/api-client';
import { Queue } from '@rossum/api-client/queues';
import { ContentGroup } from '@rossum/components/content-group/ContentGroup';
import { useSchema } from '../../../business/schema/useSchema';
import TextFieldControl from '../../../components/ReactHookForm/controls/TextFieldControl';
import { RULES_HOW_TO_LINK } from '../../../constants/values';
import { linebreak, link } from '../../../lib/formaterValues';
import { useQueue } from '../../queues/hooks/useQueue';
import { TestSection } from '../TestSection/TestSection';
import { ThenSection } from '../ThenSection';
import { ValidRuleForm } from '../utils';
import { TriggerSection } from '../WhenSection/TriggerSection';

type RuleFormProps = {
  control: Control<ValidRuleForm>;
  queue?: Queue;
  ruleQueueUrls: [Url, ...Url[]];
};

export const RuleForm = ({ control, queue, ruleQueueUrls }: RuleFormProps) => {
  const intl = useIntl();

  const triggerConditionField = useController({
    control,
    name: 'triggerCondition',
  });

  const {
    formState: {
      errors: { triggerCondition: triggerConditionValidationError },
    },
  } = useFormContext();

  // @ts-expect-error This can be partial in RHF.
  const currentRuleConcept: ValidRuleForm = useWatch({ control });

  const previewQueueUrl = queue?.url ?? ruleQueueUrls[0];
  const { data: previewQueue } = useQueue(getIDFromUrl(previewQueueUrl));
  const { data: schema, isSuccess } = useSchema(previewQueue?.schema);

  return (
    <>
      <ContentGroup
        title={intl.formatMessage({
          id: 'features.queueSettings.rules.detail.identification.title',
        })}
        description={intl.formatMessage(
          {
            id: 'features.queueSettings.rules.detail.identification.description',
          },
          {
            linebreak,
            link: link(RULES_HOW_TO_LINK),
          }
        )}
      >
        <TextFieldControl
          ControllerProps={{ control, name: 'name' }}
          label={intl.formatMessage({
            id: 'features.queueSettings.rules.detail.identification.name.label',
          })}
          FieldLabelProps={{
            layout: 'floating',
          }}
        />
        <TextFieldControl
          ControllerProps={{ control, name: 'description' }}
          label={intl.formatMessage({
            id: 'features.queueSettings.rules.detail.identification.description.label',
          })}
          FieldLabelProps={{
            layout: 'floating',
          }}
          multiline
          maxRows={3}
        />
      </ContentGroup>

      {isSuccess ? (
        <>
          <TriggerSection
            queue={previewQueueUrl}
            schemaContent={schema.content ?? []}
            schemaUrl={schema.url}
            triggerConditionField={triggerConditionField}
            currentRuleConcept={currentRuleConcept}
            // oxlint-disable-next-line @typescript-eslint/no-base-to-string
            validationError={triggerConditionValidationError?.message?.toString()}
          />
          <ThenSection control={control} schema={schema} />

          {/* There is a problem if the rules have incompatible schemas, but we need to pick one to get dependencies from. Even though the dependencies are static, we are still showing enum values / data types which can differ from queue to queue. */}
          {/* TODO: For formulas_info, maybe we don't need the whole schema in the first place: https://gitlab.rossum.cloud/elis/elis-frontend/-/merge_requests/11116#note_868291 */}
          <TestSection
            queue={queue}
            schema={schema}
            queueUrls={ruleQueueUrls}
            currentRuleConcept={currentRuleConcept}
          />
        </>
      ) : null}
    </>
  );
};
